// ABOUTME: Main investigation chat component with SSE streaming.
// ABOUTME: Handles SDK message types and renders conversation with agent.

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, Loader2, Pencil, Check, Home, Menu, ArrowLeft, Database } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { InvestigationState } from '@/types';
import type {
  SDKStreamingMessage,
  ContentBlock,
  ToolResultBlock,
  ToolUseBlock,
} from '@/types/sdk';
import {
  isAssistantMessage,
  isStreamingTextChunk,
  isSystemMessage,
  isResultMessage,
  isMessageSavedEvent,
  isToolResultBlock,
  isTextBlock,
} from '@/types/sdk';
import { streamAgentResponse } from '@/services/sse';
import { investigationApi, apiFetch } from '@/services/api/client';
import { ChatMessage } from './ChatMessage';
import { HistorySidebar } from './HistorySidebar';

interface StreamingMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string | ContentBlock[];
  isStreaming?: boolean;
  agentDurationMs?: number;
}


interface InvestigationChatProps {
  investigationId: string;
  onClose?: () => void;
}

export function InvestigationChat({ investigationId }: InvestigationChatProps) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getAccessToken } = useAuth();
  const {
    sessions,
    currentSessionId,
    startNewSession,
    loadSession,
    deleteSession,
    setInvestigationState,
    currentWorkspaceId,
  } = useApp();

  // Wrap session functions to add navigation
  const handleNewSession = useCallback(async () => {
    const newId = await startNewSession();
    navigate(`/investigations/${newId}`);
    return newId;
  }, [startNewSession, navigate]);

  const handleLoadSession = useCallback((id: string) => {
    loadSession(id);
    navigate(`/investigations/${id}`);
  }, [loadSession, navigate]);

  // Chat state
  const [messages, setMessages] = useState<StreamingMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Title/rename state
  const [investigationTitle, setInvestigationTitle] = useState('New Investigation');
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitleValue, setEditTitleValue] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // Data source names for display in the header
  const [dataSourceNames, setDataSourceNames] = useState<string[]>([]);

  // Streaming state
  const [currentBlocks, setCurrentBlocks] = useState<ContentBlock[]>([]);
  const [toolResults, setToolResults] = useState<Map<string, ToolResultBlock>>(new Map());
  const [progressMessage, setProgressMessage] = useState<string>('');

  // Ref for investigation title - used in callbacks to avoid stale closures
  const investigationTitleRef = useRef<string>('Investigation');

  // Track which investigation we've loaded to avoid re-fetching
  const loadedInvestigationRef = useRef<string | null>(null);

  // Clear messages when switching to a different investigation
  useEffect(() => {
    if (loadedInvestigationRef.current && loadedInvestigationRef.current !== investigationId) {
      // Switching to a different investigation - clear current messages
      setMessages([]);
      loadedInvestigationRef.current = null;
    }
  }, [investigationId]);

  // Default prompts shown when API fails or no data source
  const defaultPrompts = useMemo(
    () => [
      'What are the key trends in the data?',
      'Show me the most important metrics',
      'Analyze recent patterns',
    ],
    []
  );

  // Suggested prompts state - null means not yet loaded
  const [suggestedPrompts, setSuggestedPrompts] = useState<string[] | null>(null);

  // Track which investigation we've loaded prompts for to avoid re-fetching
  const loadedPromptsRef = useRef<string | null>(null);

  // Track pending query from home page (captured before history loads)
  const initialQuerySentForRef = useRef<string | null>(null);
  const pendingQueryRef = useRef<string | null>(null);

  // Refs for auto-scroll
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, currentBlocks, scrollToBottom]);

  // Load suggested prompts for this investigation (only if empty)
  const loadSuggestedPrompts = useCallback(async () => {
    // Skip if already loaded for this investigation
    if (loadedPromptsRef.current === investigationId) {
      return;
    }

    // Skip if no investigation ID or invalid ID
    if (!investigationId || investigationId.startsWith('local-')) {
      setSuggestedPrompts(defaultPrompts);
      loadedPromptsRef.current = investigationId;
      return;
    }

    try {
      const response = await investigationApi.getSuggestedPrompts(investigationId);
      if (response.prompts && response.prompts.length > 0) {
        setSuggestedPrompts(response.prompts);
      } else {
        setSuggestedPrompts(defaultPrompts);
      }
      loadedPromptsRef.current = investigationId;
    } catch (err) {
      // Silently fail - use default prompts
      console.debug('Failed to load suggested prompts:', err);
      setSuggestedPrompts(defaultPrompts);
      loadedPromptsRef.current = investigationId;
    }
  }, [investigationId, defaultPrompts]);

  // Load existing messages when opening an investigation
  useEffect(() => {
    const loadExistingMessages = async () => {
      // Skip if no investigation ID, invalid ID, or already loaded
      if (!investigationId || investigationId.startsWith('local-') || loadedInvestigationRef.current === investigationId) {
        return;
      }

      setIsLoadingHistory(true);
      try {
        const response = await investigationApi.get(investigationId);

        // Convert backend messages to frontend format
        const loadedMessages: StreamingMessage[] = response.messages.map((msg) => {
          // For assistant messages, we need to convert the content
          if (msg.role === 'assistant') {
            // Build content blocks from the backend message
            const contentBlocks: ContentBlock[] = [];

            // Add tool calls as tool_use blocks
            if (msg.tool_calls) {
              for (const toolCall of msg.tool_calls) {
                // Flatten the input: merge stored extra fields to top level
                const inputWithExtra = toolCall.input as Record<string, unknown> & { extra?: Record<string, unknown> };
                const { extra, ...baseInput } = inputWithExtra;
                const flatInput = {
                  ...baseInput,
                  ...(extra || {}),
                };

                // Infer actual tool name from the stored tool call type
                const inferredToolName: string = toolCall.type;

                contentBlocks.push({
                  type: 'tool_use',
                  id: toolCall.id,
                  name: inferredToolName,
                  input: flatInput,
                });

                // Add tool result if completed or has output
                // Status could be 'completed', 'failed', or even missing - check output presence as fallback
                const hasOutput = toolCall.output && Object.keys(toolCall.output).length > 0;
                const isFailed = toolCall.status === 'failed' || toolCall.output?.error;

                if (isFailed && toolCall.output?.error) {
                  contentBlocks.push({
                    type: 'tool_result',
                    tool_use_id: toolCall.id,
                    content: toolCall.output.error as string,
                    is_error: true,
                  });
                } else if (hasOutput) {
                  // Reconstruct the original tool_result content format from stored output.
                  // Different tool types store their data differently in ToolCallOutput.
                  let resultContent: string;
                  const outputData = toolCall.output?.data as unknown[] | undefined;
                  if (toolCall.type === 'resolve_ambiguity' && outputData?.[0]) {
                    // resolve_ambiguity: card data JSON is stored in output.data[0]
                    resultContent = JSON.stringify(outputData[0]);
                  } else if (toolCall.type === 'execute_pure_query' && outputData?.[0]) {
                    // execute_pure_query: structured result (columns, rows, query) in output.data[0]
                    resultContent = JSON.stringify(outputData[0]);
                  } else {
                    resultContent = JSON.stringify(toolCall.output);
                  }
                  // Has output data - treat as completed regardless of status field
                  contentBlocks.push({
                    type: 'tool_result',
                    tool_use_id: toolCall.id,
                    content: resultContent,
                    is_error: false,
                  });
                }
              }
            }

            // Persisted assistant content represents the assistant's final
            // response text, so place it after reconstructed tool calls.
            if (msg.content) {
              contentBlocks.push({ type: 'text', text: msg.content });
            }

            return {
              id: msg.id,
              role: 'assistant' as const,
              content: contentBlocks.length > 0 ? contentBlocks : msg.content,
              isStreaming: false,
              agentDurationMs: msg.llm_metadata?.latency_ms,
            };
          }

          // User messages just have text content
          return {
            id: msg.id,
            role: msg.role as 'user',
            content: msg.content,
            isStreaming: false,
          };
        });

        // Only set messages if we're not already streaming (avoid race condition with sendMessage)
        // Use a function updater to check current state
        setMessages((currentMessages) => {
          // If we already have messages (from sendMessage in THIS investigation), don't overwrite
          // The separate useEffect above clears messages when switching investigations,
          // so this only prevents overwriting messages from an active sendMessage in the same investigation
          if (currentMessages.length > 0) {
            return currentMessages;
          }
          return loadedMessages;
        });
        // Set the investigation title
        if (response.investigation?.title) {
          setInvestigationTitle(response.investigation.title);
        }

        // Resolve data source names for header display
        const dsIds = response.investigation?.data_source_ids;
        if (dsIds && dsIds.length > 0) {
          try {
            const profile = await apiFetch<{
              primary_workspace_id: string;
              workspaces: Array<{ workspace_id: string; data_sources?: Array<{ id: string; name: string }> }>;
            }>('/users/me');
            const ws = profile.workspaces?.find(
              (w) => w.workspace_id === currentWorkspaceId
            ) ?? profile.workspaces?.[0];
            const dsMap = new Map((ws?.data_sources ?? []).map(ds => [ds.id, ds.name]));
            setDataSourceNames(dsIds.map(id => dsMap.get(id) || 'Unknown source'));
          } catch {
            setDataSourceNames([]);
          }
        } else {
          setDataSourceNames([]);
        }

        loadedInvestigationRef.current = investigationId;

        // Only load suggested prompts if investigation is empty AND no pending query from home page
        // Check for 'q' param or pendingQueryRef to avoid fetching prompts when we're about to send a message
        const hasPendingQuery = searchParams.get('q') || pendingQueryRef.current;
        if (loadedMessages.length === 0 && !hasPendingQuery) {
          loadSuggestedPrompts();
        }
      } catch (error) {
        console.error('Failed to load investigation messages:', error);
        // Don't set loadedInvestigationRef so we can retry
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadExistingMessages();
  }, [investigationId, loadSuggestedPrompts]);

  // Focus title input when editing starts
  useEffect(() => {
    if (isEditingTitle && titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, [isEditingTitle]);

  // Handle title rename
  const handleStartEditTitle = () => {
    setEditTitleValue(investigationTitle);
    setIsEditingTitle(true);
  };

  const handleSaveTitle = async () => {
    const newTitle = editTitleValue.trim();
    if (!newTitle || newTitle === investigationTitle) {
      setIsEditingTitle(false);
      return;
    }

    try {
      await investigationApi.rename(investigationId, newTitle);
      setInvestigationTitle(newTitle);
    } catch (error) {
      console.error('Failed to rename investigation:', error);
    } finally {
      setIsEditingTitle(false);
    }
  };

  const handleTitleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveTitle();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };


  // Ref to track currentBlocks without causing re-renders
  const currentBlocksRef = useRef<ContentBlock[]>([]);

  // Handle incoming SDK messages - use refs for values that change frequently
  // to avoid recreating this callback and causing cascading re-renders
  const handleSDKMessage = useCallback((message: SDKStreamingMessage) => {
    // Handle streaming text chunks (the simple way!)
    if (isStreamingTextChunk(message)) {
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage?.role === 'assistant' && lastMessage.isStreaming) {
          // Append text chunk to last text block or create new one
          const content = lastMessage.content as ContentBlock[];
          const lastBlock = content[content.length - 1];

          if (lastBlock && isTextBlock(lastBlock)) {
            // Append to existing text block
            const updatedBlock = {
              ...lastBlock,
              text: lastBlock.text + message.content
            };
            const newContent = [...content.slice(0, -1), updatedBlock];
            return prev.map((msg, idx) =>
              idx === prev.length - 1 ? { ...msg, content: newContent } : msg
            );
          } else {
            // Create new text block
            const newBlock = { type: 'text' as const, text: message.content };
            const newContent = [...content, newBlock];
            return prev.map((msg, idx) =>
              idx === prev.length - 1 ? { ...msg, content: newContent } : msg
            );
          }
        }
        return prev;
      });
    }
    // Handle complete content blocks
    else if (isAssistantMessage(message)) {
      // Store tool results for matching with tool use
      message.content.forEach((block) => {
        if (isToolResultBlock(block)) {
          setToolResults((prev) => {
            const next = new Map(prev);
            next.set(block.tool_use_id, block);
            return next;
          });
        }
      });

      // Update the streaming assistant message with new blocks
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage?.role === 'assistant' && lastMessage.isStreaming) {
          const newContent = [...(lastMessage.content as ContentBlock[]), ...message.content];
          const updated = prev.map((msg, idx) =>
            idx === prev.length - 1
              ? { ...msg, content: newContent }
              : msg
          );
          return updated;
        }
        return prev;
      });

      // Also accumulate in ref for the result handler (doesn't trigger re-render)
      currentBlocksRef.current = [...currentBlocksRef.current, ...message.content];
      setCurrentBlocks((prev) => [...prev, ...message.content]);
    } else if (isSystemMessage(message)) {
      // Handle progress updates
      if (message.subtype === 'progress') {
        setProgressMessage(message.message);
      }
      // Handle error messages from the backend
      else if (message.subtype === 'error') {
        const errorContent: ContentBlock[] = [{
          type: 'text',
          text: message.message || 'An error occurred while processing your request.'
        }];

        // Display the error message to the user
        setMessages((prev) => {
          const lastMessage = prev[prev.length - 1];
          // If there's a streaming assistant message, replace it with the error
          if (lastMessage?.role === 'assistant' && lastMessage.isStreaming) {
            return prev.map((msg, idx) =>
              idx === prev.length - 1
                ? {
                    ...msg,
                    content: errorContent,
                    isStreaming: false,
                  }
                : msg
            );
          }
          // Otherwise add a new error message
          return [
            ...prev,
            {
              id: `error-${Date.now()}`,
              role: 'assistant',
              content: errorContent,
              isStreaming: false,
            },
          ];
        });

        // Reset streaming state
        currentBlocksRef.current = [];
        setCurrentBlocks([]);
        setProgressMessage('');
        setIsStreaming(false);
        setInvestigationState(InvestigationState.Result);
      }
    } else if (isResultMessage(message)) {
      // Finalize the assistant message - use ref to avoid stale closure
      const blocksFromRef = currentBlocksRef.current;
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage?.role === 'assistant' && lastMessage.isStreaming) {
          // Keep the existing content, just mark as not streaming
          return prev.map((msg, idx) =>
            idx === prev.length - 1
              ? { ...msg, isStreaming: false, agentDurationMs: message.duration_ms }
              : msg
          );
        }
        // If no streaming message exists, create one with current blocks
        if (blocksFromRef.length > 0) {
          return [
            ...prev,
            {
              id: `assistant-${Date.now()}`,
              role: 'assistant',
              content: blocksFromRef,
              isStreaming: false,
              agentDurationMs: message.duration_ms,
            },
          ];
        }
        return prev;
      });

      // Reset streaming state
      currentBlocksRef.current = [];
      setCurrentBlocks([]);
      setProgressMessage('');
      setInvestigationState(InvestigationState.Result);

      // Handle errors
      if (message.is_error) {
        console.error('Agent error:', message.errors);
      }
    } else if (isMessageSavedEvent(message)) {
      // Update the last assistant message's ID with the real MongoDB ID
      setMessages((prev) => {
        // Find last assistant message index
        let lastAssistantIdx = -1;
        for (let i = prev.length - 1; i >= 0; i--) {
          if (prev[i].role === 'assistant') {
            lastAssistantIdx = i;
            break;
          }
        }
        if (lastAssistantIdx === -1) return prev;

        return prev.map((msg, idx) =>
          idx === lastAssistantIdx
            ? { ...msg, id: message.message_id }
            : msg
        );
      });
    }
  }, [setInvestigationState]);


  // Send a message to the agent
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim() || isStreaming) return;

    const trimmedContent = content.trim();

    // Add user message AND streaming assistant placeholder in a single update
    // This prevents React batching issues where the assistant message gets lost
    const userMessage: StreamingMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmedContent,
    };

    const assistantMessage: StreamingMessage = {
      id: `assistant-${Date.now() + 1}`,
      role: 'assistant',
      content: [],
      isStreaming: true,
    };

    setMessages((prev) => [...prev, userMessage, assistantMessage]);

    // Reset streaming state for new message
    currentBlocksRef.current = [];
    setCurrentBlocks([]);
    setToolResults(new Map());
    setProgressMessage('');
    setInvestigationState(InvestigationState.Thinking);
    setIsStreaming(true);

    // Clear input
    setInputValue('');

    try {
      // Use SSE streaming for real API call
      const token = await getAccessToken();

      for await (const message of streamAgentResponse(investigationId, trimmedContent, token, currentWorkspaceId)) {
        handleSDKMessage(message);
      }
    } catch (error) {
      console.error('Streaming error:', error);
      setMessages((prev) => [
        ...prev,
        {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: 'Sorry, an error occurred while processing your message.',
        },
      ]);
    } finally {
      setIsStreaming(false);
      setInvestigationState(InvestigationState.Result);
    }
  }, [investigationId, getAccessToken, handleSDKMessage, isStreaming, setInvestigationState, currentWorkspaceId]);

  // Handle form submission
  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    sendMessage(inputValue);
  };

  // Handle query parameter from home page - auto-send the message
  // Capture the query param immediately on mount/navigation
  // This runs first to capture the query before other effects
  useEffect(() => {
    const query = searchParams.get('q');
    if (query && initialQuerySentForRef.current !== investigationId) {
      pendingQueryRef.current = query;
      // Clear the query param from URL immediately
      setSearchParams({}, { replace: true });
    }
  }, [investigationId, searchParams, setSearchParams]);

  // Send the pending query once history is done loading and we have no messages
  useEffect(() => {
    if (
      pendingQueryRef.current &&
      initialQuerySentForRef.current !== investigationId &&
      !isStreaming &&
      !isLoadingHistory &&
      messages.length === 0
    ) {
      const queryToSend = pendingQueryRef.current;
      initialQuerySentForRef.current = investigationId;
      pendingQueryRef.current = null;
      // Send the message immediately - no delay needed
      sendMessage(queryToSend);
    }
  }, [investigationId, isStreaming, isLoadingHistory, messages.length, sendMessage]);

  // Handle smart action clicks - sends a message to the LLM
  const handleSmartAction = useCallback((action: { label: string; query?: string | null }) => {
    // Use the query if available, otherwise fall back to the label
    const message = action.query || action.label;
    sendMessage(message);
  }, [sendMessage]);

  // Get current investigation title
  const currentInvestigationTitle = useMemo(() => {
    const session = sessions.find(s => s.id === investigationId);
    return session?.title || 'Investigation';
  }, [sessions, investigationId]);

  // Keep ref in sync for stable callback access
  investigationTitleRef.current = currentInvestigationTitle;

  return (
    <div className="flex flex-col h-full bg-surface-50">
      {/* History Sidebar */}
      <HistorySidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        sessions={sessions}
        currentSessionId={currentSessionId}
        onNewSession={handleNewSession}
        onLoadSession={handleLoadSession}
        onDeleteSession={deleteSession}
      />

      {/* Top Bar */}
      <div className="flex items-center justify-between gap-3 px-3 py-2 border-b border-surface-200 bg-surface-0">
        {/* Menu Button */}
        <button
          onClick={() => setIsSidebarOpen(true)}
          className="p-1.5 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-800 transition-colors cursor-pointer"
          title="View history"
        >
          <Menu className="w-4 h-4" />
        </button>

        {/* Investigation Title + Data Sources */}
        <div className="min-w-0 flex flex-1 items-center justify-center gap-2">
          <div className="flex min-w-0 items-center gap-1.5">
            {isEditingTitle ? (
              <div className="flex items-center gap-2">
                <input
                  ref={titleInputRef}
                  type="text"
                  value={editTitleValue}
                  onChange={(e) => setEditTitleValue(e.target.value)}
                  onKeyDown={handleTitleKeyDown}
                  onBlur={handleSaveTitle}
                  className="bg-surface-50 border border-surface-200 rounded-sm px-2.5 py-1 text-surface-800 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Investigation title"
                />
                <button
                  onClick={handleSaveTitle}
                  className="p-1 rounded-sm hover:bg-surface-100 text-success transition-colors cursor-pointer"
                >
                  <Check className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <>
                <h1 className="truncate text-sm font-medium text-surface-800">{investigationTitle}</h1>
                <button
                  onClick={handleStartEditTitle}
                  className="p-1 rounded-sm hover:bg-surface-100 text-surface-400 hover:text-surface-800 transition-colors cursor-pointer"
                  title="Rename investigation"
                >
                  <Pencil className="w-3.5 h-3.5" />
                </button>
              </>
            )}
          </div>
          {dataSourceNames.length > 0 && !isEditingTitle && (
            <div className="hidden min-w-0 items-center gap-1.5 sm:flex">
              {dataSourceNames.map((name, i) => (
                <span
                  key={i}
                  className="inline-flex min-w-0 items-center gap-1 rounded bg-surface-100 px-1.5 py-0.5 text-[11px] text-surface-500"
                  title={name}
                >
                  <Database className="h-3 w-3 shrink-0" />
                  <span className="truncate">{name}</span>
                </span>
              ))}
            </div>
          )}
          {!isEditingTitle && (
            <span className="hidden shrink-0 text-[11px] text-surface-400 md:inline">
              Conversation history isn't stored
            </span>
          )}
        </div>

        {/* Spacer to balance layout */}
        <div className="w-7" />
      </div>

      {/* Messages Area - uses flex-col-reverse to anchor content to bottom */}
      <div className="flex-1 overflow-y-auto px-6 pt-4 pb-4 flex flex-col-reverse">
        <div className="max-w-4xl mx-auto space-y-4 w-full">
          {/* Loading History State */}
          {isLoadingHistory && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center mt-20"
            >
              <Loader2 className="w-8 h-8 animate-spin text-primary-500 mx-auto mb-4" />
              <p className="text-surface-500">Loading conversation history...</p>
            </motion.div>
          )}

          {/* Empty State */}
          {messages.length === 0 && !isStreaming && !isLoadingHistory && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mt-20"
            >
              <h2 className="text-3xl font-sans font-medium text-surface-800 mb-2 tracking-tight">
                How can I help you today?
              </h2>
              <p className="text-surface-500 mb-8">
                Ask me a question about your data.
              </p>
              <div className="flex flex-wrap justify-center gap-3 min-h-[44px]">
                {suggestedPrompts ? (
                  suggestedPrompts.map((suggestion) => (
                    <button
                      key={suggestion}
                      onClick={() => sendMessage(suggestion)}
                      className="px-5 py-2.5 rounded-sm bg-surface-100 hover:bg-surface-200 text-sm text-surface-600 hover:text-surface-800 transition-all cursor-pointer"
                    >
                      {suggestion}
                    </button>
                  ))
                ) : (
                  <div className="flex items-center gap-2 text-surface-400 text-sm">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>Loading suggestions...</span>
                  </div>
                )}
              </div>
            </motion.div>
          )}

          {/* Message List */}
          {(() => {
            // Find the most recent assistant message (for showing smart actions only on latest)
            const lastAssistantMsgId = [...messages].reverse().find(m => m.role === 'assistant')?.id;

            return messages.map((msg, msgIndex) => {
              // Only show smart actions on the most recent assistant message
              const showSmartActions = msg.id === lastAssistantMsgId;

              // Detect resolved clarification: if this assistant message has a
              // resolve_ambiguity tool call and the next message is a user message,
              // the user already answered — pass their text as the resolved label.
              let clarificationResolvedLabel: string | undefined;
              if (msg.role === 'assistant' && !msg.isStreaming && Array.isArray(msg.content)) {
                const hasClarification = (msg.content as ContentBlock[]).some(
                  (block) => block.type === 'tool_use' && (
                    (block as ToolUseBlock).name === 'resolve_ambiguity' ||
                    (block as ToolUseBlock).name?.endsWith('__resolve_ambiguity')
                  )
                );
                if (hasClarification) {
                  const nextMsg = messages[msgIndex + 1];
                  if (nextMsg && nextMsg.role === 'user' && typeof nextMsg.content === 'string') {
                    clarificationResolvedLabel = nextMsg.content;
                  }
                }
              }

              return (
                <div key={msg.id}>
                  <ChatMessage
                    messageId={msg.id}
                    role={msg.role}
                    content={msg.content}
                    toolResults={toolResults}
                    agentDurationMs={msg.agentDurationMs}
                    isStreaming={msg.isStreaming}
                    currentPhase={progressMessage}
                    showSmartActions={showSmartActions}
                    onSmartAction={handleSmartAction}
                    onSendFollowUp={sendMessage}
                    clarificationResolvedLabel={clarificationResolvedLabel}
                  />
                </div>
              );
            });
          })()}

          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input Area */}
      <div className="px-4 py-10 bg-surface-0 border-t border-surface-200">
        <div className="max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="relative">
            <div className="flex items-center bg-surface-0 border border-surface-200 rounded-sm shadow-sm">
              {/* Back button */}
              <button
                type="button"
                onClick={() => navigate('/investigations')}
                className="pl-4 pr-3 py-4 text-surface-400 hover:text-surface-800 transition-colors shrink-0 cursor-pointer"
                title="Back to investigations"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>

              {/* Divider */}
              <div className="w-px h-5 bg-surface-200 my-auto" />

              {/* Home button */}
              <button
                type="button"
                onClick={() => navigate('/')}
                className="px-3 py-4 text-surface-400 hover:text-surface-800 transition-colors shrink-0 cursor-pointer"
                title="Go to home"
              >
                <Home className="w-5 h-5" />
              </button>

              {/* Divider */}
              <div className="w-px h-5 bg-surface-200 my-auto" />

              {/* Input field */}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                placeholder={isStreaming ? 'Waiting for response...' : 'Ask a question...'}
                disabled={isStreaming}
                className="flex-1 bg-transparent py-4 pl-3 pr-3 text-base text-surface-800 placeholder-surface-400 focus:outline-none focus:ring-0 focus:border-transparent border-none disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ outline: 'none', border: 'none', boxShadow: 'none' }}
                autoFocus
              />

              {/* Submit button */}
              <button
                type="submit"
                disabled={!inputValue.trim() || isStreaming}
                className="m-1.5 p-2.5 bg-primary-500 hover:bg-primary-600 rounded-sm text-white shadow-md cursor-pointer transition-all disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none shrink-0"
                aria-label="Send message"
              >
                {isStreaming ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <ArrowUp className="w-5 h-5" />
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
